import React, { useState, useEffect } from 'react';
import { therapistApi, studentApi, assignmentApi, guestStudentApi } from '../../services/api';
import { Assignment, Therapist, Student, GuestStudent, TimeSlot } from '../../types';
import './AssignmentModal.css';

interface AssignmentModalProps {
  isOpen: boolean;
  assignment?: Assignment | null;
  timeSlot: TimeSlot;
  day: string;
  scheduleId: number;
  schedule?: any; // Add schedule prop to get therapist info
  onSave: () => void;
  onClose: () => void;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  assignment,
  timeSlot,
  day,
  scheduleId,
  schedule,
  onSave,
  onClose
}) => {
  const [formData, setFormData] = useState({
    therapistId: '',
    studentId: '',
    assignmentType: 'therapy' as 'therapy' | 'break' | 'group_activity',
    activityName: '',
    notes: ''
  });
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [guestStudents, setGuestStudents] = useState<GuestStudent[]>([]);
  const [guestStudentsLoading, setGuestStudentsLoading] = useState(false);
  const [studentMode, setStudentMode] = useState<'registered' | 'guest'>('registered');
  const [selectedGuestId, setSelectedGuestId] = useState<string>('');
  const [guestForm, setGuestForm] = useState({
    fullName: '',
    contactInfo: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (assignment) {
      setFormData({
        therapistId: assignment.therapistId?.toString() || '',
        studentId: assignment.studentId?.toString() || '',
        assignmentType: assignment.assignmentType,
        activityName: assignment.activityName || '',
        notes: assignment.notes || ''
      });

      if (assignment.studentId) {
        setStudentMode('registered');
        setSelectedGuestId('');
        setGuestForm({
          fullName: '',
          contactInfo: '',
          notes: ''
        });
      } else if (assignment.guestStudent) {
        setStudentMode('guest');
        setSelectedGuestId(assignment.guestStudent.id ? assignment.guestStudent.id.toString() : '');
        setGuestForm({
          fullName: assignment.guestStudent.fullName || '',
          contactInfo: assignment.guestStudent.contactInfo || '',
          notes: assignment.guestStudent.notes || ''
        });
      } else {
        setStudentMode('registered');
        setSelectedGuestId('');
        setGuestForm({ fullName: '', contactInfo: '', notes: '' });
      }
    } else {
      // Pre-fill therapist from schedule when creating new assignment
      setFormData({
        therapistId: schedule?.therapistId?.toString() || '',
        studentId: '',
        assignmentType: 'therapy',
        activityName: '',
        notes: ''
      });
      setStudentMode(schedule?.therapistId ? 'registered' : 'registered');
      setSelectedGuestId('');
      setGuestForm({ fullName: '', contactInfo: '', notes: '' });
    }
  }, [assignment, schedule]);

  const loadData = async () => {
    setLoadingData(true);
    setGuestStudentsLoading(true);
    try {
      const [therapistResponse, studentResponse, guestResponse] = await Promise.all([
        therapistApi.getAll({ active: true, limit: 100 }),
        studentApi.getAll({ active: true, limit: 100 }),
        guestStudentApi.getAll({ limit: 200 })
      ]);
      
      setTherapists(therapistResponse.data.therapists);
      setStudents(studentResponse.data.students);
      setGuestStudents(guestResponse.data.guestStudents);
    } catch (err: any) {
      console.error('Error loading assignment modal data:', err);
      setError(err.error || 'Failed to load data');
    } finally {
      setLoadingData(false);
      setGuestStudentsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStudentModeChange = (mode: 'registered' | 'guest') => {
    setStudentMode(mode);
    if (mode === 'registered') {
      setSelectedGuestId('');
      setGuestForm({ fullName: '', contactInfo: '', notes: '' });
    } else {
      setFormData(prev => ({ ...prev, studentId: '' }));
    }
  };

  const handleGuestSelectChange = (value: string) => {
    setSelectedGuestId(value);
    if (!value) {
      setGuestForm({ fullName: '', contactInfo: '', notes: '' });
      return;
    }

    const guest = guestStudents.find(g => g.id === Number(value));
    if (guest) {
      setGuestForm({
        fullName: guest.fullName,
        contactInfo: guest.contactInfo || '',
        notes: guest.notes || ''
      });
    }
  };

  const handleGuestFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setGuestForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to calculate the actual date for the assignment
  const getAssignmentDate = () => {
    if (!schedule) return new Date().toISOString().split('T')[0];
    
    const startDate = new Date(schedule.weekStartDate);
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayIndex = daysOfWeek.indexOf(day);
    
    if (dayIndex === -1) return new Date().toISOString().split('T')[0];
    
    const assignmentDate = new Date(startDate);
    assignmentDate.setDate(startDate.getDate() + dayIndex);
    
    return assignmentDate.toISOString().split('T')[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Ensure time format is correct (HH:MM:SS)
      const formatTime = (time: string): string => {
        if (!time) return time;
        const parts = time.split(':');
        if (parts.length === 2) {
          return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
        } else if (parts.length === 3) {
          return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
        }
        return time;
      };

      const isTherapy = formData.assignmentType === 'therapy';
      const isGuestAssignment = isTherapy && studentMode === 'guest';
      const therapistIdValue = isTherapy && formData.therapistId ? parseInt(formData.therapistId, 10) : undefined;
      const studentIdValue = isTherapy && !isGuestAssignment && formData.studentId ? parseInt(formData.studentId, 10) : undefined;
      const guestIdValue = isGuestAssignment && selectedGuestId ? parseInt(selectedGuestId, 10) : undefined;
      const trimmedGuestName = guestForm.fullName.trim();

      if (isGuestAssignment && !guestIdValue && trimmedGuestName.length === 0) {
        setError('Guest student name is required');
        setLoading(false);
        return;
      }

      const assignmentData: Partial<Assignment> = {
        scheduleId,
        therapistId: therapistIdValue,
        studentId: studentIdValue,
        guestStudentId: isGuestAssignment ? guestIdValue : undefined,
        assignmentDate: getAssignmentDate(),
        startTime: formatTime(timeSlot.startTime),
        endTime: formatTime(timeSlot.endTime),
        dayOfWeek: day,
        assignmentType: formData.assignmentType,
        activityName: formData.assignmentType === 'therapy' ? formData.activityName || undefined : formData.activityName || undefined,
        notes: formData.notes || undefined
      };

      if (isGuestAssignment) {
        const contactTrimmed = guestForm.contactInfo.trim();
        const notesTrimmed = guestForm.notes.trim();
        let guestPayload: GuestStudent | undefined;

        if (guestIdValue) {
          const existingGuest = guestStudents.find(g => g.id === guestIdValue);
          const targetName = trimmedGuestName || existingGuest?.fullName || '';
          if (!targetName) {
            setError('Guest student name is required');
            setLoading(false);
            return;
          }

          const detailsChanged =
            targetName !== (existingGuest?.fullName || '') ||
            contactTrimmed !== (existingGuest?.contactInfo || '') ||
            notesTrimmed !== (existingGuest?.notes || '');

          if (detailsChanged) {
            guestPayload = {
              id: guestIdValue,
              fullName: targetName,
              contactInfo: contactTrimmed || undefined,
              notes: notesTrimmed || undefined
            };
          }
        } else {
          if (!trimmedGuestName) {
            setError('Guest student name is required');
            setLoading(false);
            return;
          }

          guestPayload = {
            fullName: trimmedGuestName,
            contactInfo: contactTrimmed || undefined,
            notes: notesTrimmed || undefined
          };
        }

        if (guestPayload) {
          assignmentData.guestStudent = guestPayload;
        } else {
          delete assignmentData.guestStudent;
        }
      } else {
        delete assignmentData.guestStudent;
      }

      if (!isTherapy) {
        assignmentData.studentId = undefined;
        assignmentData.guestStudentId = undefined;
        delete assignmentData.guestStudent;
      }

      console.log('=== ASSIGNMENT SUBMISSION DEBUG ===');
      console.log('Submitting assignment data:', assignmentData);
      console.log('Schedule data:', schedule);
      console.log('Time slot data:', timeSlot);
      console.log('Form data:', formData);
      console.log('Assignment date calculated:', getAssignmentDate());
      console.log('Day:', day);
      console.log('Schedule ID:', scheduleId);

      let response;
      if (assignment) {
        console.log('Updating existing assignment:', assignment.id);
        response = await assignmentApi.update(assignment.id, assignmentData);
      } else {
        console.log('Creating new assignment');
        response = await assignmentApi.create(assignmentData);
      }
      
      console.log('Assignment API response:', response);
      onSave();
    } catch (err: any) {
      console.error('=== ASSIGNMENT SUBMISSION ERROR ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Full error JSON:', JSON.stringify(err, null, 2));
      
      // Show more detailed error information
      let errorMessage = 'Something went wrong!';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.error) {
        errorMessage = err.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (err.response?.data?.details && Array.isArray(err.response.data.details)) {
        errorMessage += '\n\nDetails:\n' + err.response.data.details.join('\n');
      } else if (err.details && Array.isArray(err.details)) {
        errorMessage += '\n\nDetails:\n' + err.details.join('\n');
      }
      
      console.error('Final error message to display:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!assignment) return;
    
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      setLoading(true);
      try {
        await assignmentApi.delete(assignment.id);
        onSave();
      } catch (err: any) {
        setError(err.error || 'Failed to delete assignment');
      } finally {
        setLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content assignment-modal">
        <div className="modal-header">
          <h3>
            {assignment ? 'Edit Assignment' : 'Create Assignment'}
          </h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="assignment-info">
          <div className="time-info">
            <strong>{day}</strong> • {timeSlot.startTime} - {timeSlot.endTime}
          </div>
          {timeSlot.slotName && (
            <div className="slot-info">{timeSlot.slotName}</div>
          )}
        </div>

        {loadingData ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="assignment-form">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="assignmentType">Assignment Type *</label>
              <select
                id="assignmentType"
                name="assignmentType"
                value={formData.assignmentType}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="therapy">Therapy Session</option>
                <option value="break">Break</option>
                <option value="group_activity">Group Activity</option>
              </select>
            </div>

            {formData.assignmentType === 'therapy' && (
              <>
                <div className="form-group">
                  <label htmlFor="therapistId">Therapist *</label>
                  <select
                    id="therapistId"
                    name="therapistId"
                    value={formData.therapistId}
                    onChange={handleChange}
                    className="form-input"
                    disabled={schedule && schedule.therapistId && !assignment} // Disable when creating new assignment in existing schedule
                    required
                  >
                    <option value="">Select Therapist</option>
                    {therapists.map(therapist => (
                      <option key={therapist.id} value={therapist.id}>
                        {therapist.name} {therapist.specialization && `(${therapist.specialization})`}
                      </option>
                    ))}
                  </select>
                  {schedule && schedule.therapistId && !assignment && (
                    <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                      Therapist is pre-selected from the current schedule
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Participant *</label>
                  <div className="participant-toggle">
                    <label className="participant-option">
                      <input
                        type="radio"
                        name="participantType"
                        value="registered"
                        checked={studentMode === 'registered'}
                        onChange={() => handleStudentModeChange('registered')}
                      />
                      <span>Registered Student</span>
                    </label>
                    <label className="participant-option">
                      <input
                        type="radio"
                        name="participantType"
                        value="guest"
                        checked={studentMode === 'guest'}
                        onChange={() => handleStudentModeChange('guest')}
                      />
                      <span>Guest Student</span>
                    </label>
                  </div>
                </div>

                {studentMode === 'registered' && (
                  <div className="form-group">
                    <label htmlFor="studentId">Select Student *</label>
                    <select
                      id="studentId"
                      name="studentId"
                      value={formData.studentId}
                      onChange={handleChange}
                      className="form-input"
                      required={studentMode === 'registered'}
                    >
                      <option value="">Select Student</option>
                      {students.map(student => (
                        <option key={student.id} value={student.id}>
                          {(student as any).name ||
                           (student.first_name && student.last_name ?
                             `${student.first_name} ${student.last_name}` :
                             `Student ${student.id}`
                           )} {student.grade_level && `(${student.grade_level})`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {studentMode === 'guest' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="guestStudentSelect">Existing Guest</label>
                      <select
                        id="guestStudentSelect"
                        value={selectedGuestId}
                        onChange={(e) => handleGuestSelectChange(e.target.value)}
                        className="form-input"
                        disabled={guestStudentsLoading}
                      >
                        <option value="">Create New Guest</option>
                        {guestStudents.map(guest => (
                          <option key={guest.id} value={guest.id}>{guest.fullName}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="guestFullName">Guest Name *</label>
                      <input
                        type="text"
                        id="guestFullName"
                        name="fullName"
                        value={guestForm.fullName}
                        onChange={handleGuestFormChange}
                        className="form-input"
                        placeholder="e.g., John Doe"
                        required={studentMode === 'guest' && !selectedGuestId}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="guestContact">Contact Info</label>
                      <input
                        type="text"
                        id="guestContact"
                        name="contactInfo"
                        value={guestForm.contactInfo}
                        onChange={handleGuestFormChange}
                        className="form-input"
                        placeholder="Phone or email"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="guestNotes">Notes</label>
                      <textarea
                        id="guestNotes"
                        name="notes"
                        value={guestForm.notes}
                        onChange={handleGuestFormChange}
                        className="form-textarea"
                        rows={2}
                        placeholder="Additional details about the guest student..."
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {(formData.assignmentType === 'break' || formData.assignmentType === 'group_activity') && (
              <div className="form-group">
                <label htmlFor="activityName">
                  {formData.assignmentType === 'break' ? 'Break Name' : 'Activity Name'} *
                </label>
                <input
                  type="text"
                  id="activityName"
                  name="activityName"
                  value={formData.activityName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder={formData.assignmentType === 'break' ? 'e.g., Lunch Break' : 'e.g., P.E., Nursery'}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-textarea"
                rows={3}
                placeholder="Additional notes or special instructions..."
              />
            </div>


            <div className="form-actions">
              {assignment && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="delete-button"
                  disabled={loading}
                >
                  Delete
                </button>
              )}
              <div className="action-buttons">
                <button type="button" onClick={onClose} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="submit-button">
                  {loading ? 'Saving...' : (assignment ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AssignmentModal;
